// components/project/ProjectCardMobile.tsx
import React from 'react';
import { View, Text, TouchableOpacity, Image } from 'react-native';
import styles from '../../styles/pages/projectstatusStyles';
import { Project } from '../../types/projectstatus';
import TimelineChart from './TimelineChart';

interface ProjectCardMobileProps {
  project: Project;
  isExpanded: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}

interface ProgressInfo {
  actualProgress: number;
  expectedProgress: number;
  progressDifference: number;
}

const formatDoneRatio = (value: number | string): string => {
  // 숫자로 변환
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // 정수인 경우 소수점 제거
  if (Number.isInteger(numValue)) {
    return numValue.toString();
  }
  
  // 소수점이 있는 경우 그대로 표시
  return numValue.toString();
};

const calculateProjectProgress = (project: Project): ProgressInfo => {
  const startDateValue = project.custom_fields.find(
    (field) => field.name === '시작일',
  )?.value;
  const endDateValue = project.custom_fields.find(
    (field) => field.name === '종료일',
  )?.value;
  const executionStartDateValue = project.custom_fields.find(
    (field) => field.name === '착수 시작일',
  )?.value;
  const executionEndDateValue = project.custom_fields.find(
    (field) => field.name === '착수 종료일',
  )?.value;

  if (
    !startDateValue &&
    !endDateValue &&
    !executionStartDateValue &&
    !executionEndDateValue
  ) {
    return {
      actualProgress: project.done_ratio,
      expectedProgress: 0,
      progressDifference: 0,
    };
  }

  const startDate = startDateValue ? new Date(startDateValue) : new Date();
  const endDate = endDateValue ? new Date(endDateValue) : new Date();
  const executionStartDate = executionStartDateValue
    ? new Date(executionStartDateValue)
    : new Date();
  const executionEndDate = executionEndDateValue
    ? new Date(executionEndDateValue)
    : new Date();
  const today = new Date();

  const validDates = [
    startDateValue ? startDate : null,
    endDateValue ? endDate : null,
    executionStartDateValue ? executionStartDate : null,
    executionEndDateValue ? executionEndDate : null,
  ].filter((date): date is Date => date !== null);

  if (validDates.length < 2) {
    return {
      actualProgress: project.done_ratio,
      expectedProgress: 0,
      progressDifference: 0,
    };
  }

  const minStartDate = new Date(
    Math.min(...validDates.map((d) => d.getTime())),
  );
  const maxEndDate = new Date(Math.max(...validDates.map((d) => d.getTime())));

  const totalDays = Math.ceil(
    (maxEndDate.getTime() - minStartDate.getTime()) / (1000 * 3600 * 24),
  );
  const elapsedDays = Math.ceil(
    (today.getTime() - minStartDate.getTime()) / (1000 * 3600 * 24),
  );

  const rawExpectedProgress = totalDays > 0 ? (elapsedDays / totalDays) * 100 : 0;
  // 100%를 초과하면 100%로 고정
  const expectedProgress = Math.min(rawExpectedProgress, 100);
  const actualProgress = project.done_ratio;
  const progressDifference = expectedProgress - actualProgress;

  return {
    actualProgress,
    expectedProgress,
    progressDifference,
  };
};

const getProjectStatus = (progressInfo: ProgressInfo): string => {
  const difference =
    progressInfo.expectedProgress - progressInfo.actualProgress;

  if (difference > 15) {
    return '지연';
  } else if (difference >= 11) {
    return '주의';
  }
  return '양호';
};

export default function ProjectCardMobile({ project, isExpanded, onToggle, children }: ProjectCardMobileProps) {
  const progressInfo = calculateProjectProgress(project);
  const status = getProjectStatus(progressInfo);

  const openRedmineLink = () => {
    window.open(
      `https://redmine.idpass.kr/projects/${project.identifier}`,
      '_blank',
    );
  };

  // 계약기간 포맷팅 함수
  const getContractPeriod = () => {
    const startDateValue = project.custom_fields.find(
      (field) => field.name === '시작일',
    )?.value;
    const endDateValue = project.custom_fields.find(
      (field) => field.name === '종료일',
    )?.value;

    if (!startDateValue && !endDateValue) return '-';

    const formatPeriodDate = (dateValue: string | undefined) => {
      if (!dateValue) return '';
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    };

    const startFormatted = formatPeriodDate(startDateValue);
    const endFormatted = formatPeriodDate(endDateValue);

    if (startFormatted && endFormatted) {
      return `${startFormatted} ~ ${endFormatted}`;
    } else if (startFormatted) {
      return startFormatted;
    } else if (endFormatted) {
      return endFormatted;
    }
    return '-';
  };

  // 착수기간 포맷팅 함수
  const getExecutionPeriod = () => {
    const startDateValue = project.custom_fields.find(
      (field) => field.name === '착수 시작일',
    )?.value;
    const endDateValue = project.custom_fields.find(
      (field) => field.name === '착수 종료일',
    )?.value;

    if (!startDateValue && !endDateValue) return '-';

    const formatPeriodDate = (dateValue: string | undefined) => {
      if (!dateValue) return '';
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    };

    const startFormatted = formatPeriodDate(startDateValue);
    const endFormatted = formatPeriodDate(endDateValue);

    if (startFormatted && endFormatted) {
      return `${startFormatted} ~ ${endFormatted}`;
    } else if (startFormatted) {
      return startFormatted;
    } else if (endFormatted) {
      return endFormatted;
    }
    return '-';
  };

  const getProjectType = () => {
    return project.custom_fields.find(
      (field) => field.name === '프로젝트 구분',
    )?.value || '-';
  };

  const getTotalPeriod = () => {
    const startDateValue = project.custom_fields.find(
      (field) => field.name === '시작일',
    )?.value;
    const endDateValue = project.custom_fields.find(
      (field) => field.name === '종료일',
    )?.value;
    const executionStartDateValue = project.custom_fields.find(
      (field) => field.name === '착수 시작일',
    )?.value;
    const executionEndDateValue = project.custom_fields.find(
      (field) => field.name === '착수 종료일',
    )?.value;

    if (
      !startDateValue &&
      !endDateValue &&
      !executionStartDateValue &&
      !executionEndDateValue
    ) {
      return '-';
    }

    const startDate = startDateValue ? new Date(startDateValue) : null;
    const endDate = endDateValue ? new Date(endDateValue) : null;
    const executionStartDate = executionStartDateValue
      ? new Date(executionStartDateValue)
      : null;
    const executionEndDate = executionEndDateValue
      ? new Date(executionEndDateValue)
      : null;

    const validDates = [
      startDate,
      endDate,
      executionStartDate,
      executionEndDate,
    ].filter((date): date is Date => date !== null);
    
    if (validDates.length === 0) return '-';

    const minStartDate = new Date(
      Math.min(...validDates.map((d) => d.getTime())),
    );
    const maxEndDate = new Date(
      Math.max(...validDates.map((d) => d.getTime())),
    );

    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    };

    return validDates.length >= 2
      ? `${formatDate(minStartDate)} ~ ${formatDate(maxEndDate)}`
      : formatDate(validDates[0]);
  };

  // 프로젝트 이름 줄이기 (첫 부분만 표시)
  const getShortProjectName = (name: string) => {
    // 하이픈이나 특정 구분자를 기준으로 첫 부분만 표시
    const parts = name.split(/[-]/);
    if (parts.length > 1) {
      return `${parts[0].trim()}-${parts[1].trim()}`;
    }
    return name;
  };

  return (
    <View style={styles.projectWrapper}>
      <View style={styles.projectContainer}>
        <TouchableOpacity onPress={onToggle}>
          <View style={styles.projectTitleRow}>
            <Text style={styles.projectName}>{getShortProjectName(project.name)}</Text>
            <Text style={styles.caret}>
              {isExpanded ? '⌄' : '⌃'}
            </Text>
          </View>
          <View style={styles.progressRow}>
            <Text style={styles.projectPercent}>{formatDoneRatio(project.done_ratio)}%</Text>
            <Text style={styles.expectedProgress}>
              {progressInfo.expectedProgress > 0
                ? `(예상 진행률: ${Math.round(progressInfo.expectedProgress)}%)`
                : '(예상 진행률: -)'}
            </Text>
            <View
              style={[
                styles.statusIndicator,
                status === '지연' && styles.dangerIndicator,
                status === '주의' && styles.warningIndicator,
                status === '양호' && styles.goodIndicator,
              ]}
            />
            <Text
              style={[
                styles.statusText,
                status === '지연' && styles.dangerText,
                status === '주의' && styles.warningText,
                status === '양호' && styles.goodText,
              ]}
            >
              {status}
            </Text>
            <TouchableOpacity
              onPress={openRedmineLink}
              style={styles.redmineIconButton}
            >
              <Image
                source={require('../../assets/redmine.png')}
                style={{ width: 16, height: 16 }}
                resizeMode="contain"
              />
              <Text style={styles.redmineText}>Redmine</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
        
        <View style={styles.projectDetails}>
          <Text style={styles.detailText}>
            총기간: {getTotalPeriod()}
          </Text>
          <Text style={styles.detailText}>
            계약기간: {getContractPeriod()}
          </Text>
          <Text style={styles.detailText}>
            착수기간: {getExecutionPeriod()}
          </Text>
          <Text style={styles.detailText}>
            프로젝트 구분: {getProjectType()}
          </Text>
        </View>
      </View>

      <TimelineChart project={project} />
      
      {/* 진행률과 뉴스는 상세보기를 눌렀을 때만 표시 */}
      {isExpanded && children}
    </View>
  );
}